/**
 * EPIC 45 — React-query-free live hooks over the leave request subscriptions.
 * Mirror the useWorker.ts pattern (useState + onSnapshot). Used by the worker
 * "Puhkuse taotlus" page, the admin review queue, and the admin dashboard
 * pending-count card.
 */

import type { LeaveRequest } from '@rendimees24/shared-types';
import { useEffect, useState } from 'react';

import {
  subscribeMyLeaveRequests,
  subscribePendingLeaveRequests,
  subscribeUnfinishedLeaveReleases,
} from './leaveRequests';

export function useMyLeaveRequests(uid: string | undefined) {
  const [state, setState] = useState<{
    uid: string;
    requests: LeaveRequest[];
    error: Error | null;
  } | null>(null);
  useEffect(() => {
    if (!uid) return;
    return subscribeMyLeaveRequests(
      uid,
      (requests) => setState({ uid, requests, error: null }),
      (error) => setState({ uid, requests: [], error }),
    );
  }, [uid]);
  const current = state?.uid === uid ? state : null;
  return {
    requests: current?.requests ?? [],
    loading: !!uid && !current,
    error: current?.error ?? null,
  };
}

export function usePendingLeaveRequests() {
  const [requests, setRequests] = useState<LeaveRequest[] | null>(null);

  useEffect(() => {
    const unsub = subscribePendingLeaveRequests(setRequests);
    return () => {
      try {
        unsub();
      } catch {
        /* ignore */
      }
    };
  }, []);

  return { requests: requests ?? [], loading: requests === null };
}

/** 0.4 — approved requests whose staffing release still needs RM24. */
export function useUnfinishedLeaveReleases() {
  const [requests, setRequests] = useState<LeaveRequest[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    const unsub = subscribeUnfinishedLeaveReleases(setRequests, (e) => {
      setError(e);
      setRequests([]);
    });
    return () => {
      try {
        unsub();
      } catch {
        /* ignore */
      }
    };
  }, []);
  return { requests: requests ?? [], loading: requests === null, error };
}
